import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react";
import { useMediaQuery } from "@mui/material";
import Button, { type ButtonSize, type ButtonVariant } from "./Button";
import IconButton from "./IconButton";
import { cn } from "./utils/cn";

const DotsVerticalIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
    width="1em"
    height="1em"
  >
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

export interface MenuButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  /** Text shown on desktop. Collapses to an icon-only button on mobile. */
  label: string;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  /** Trigger icon. Defaults to a vertical "⋮" (more) icon. */
  icon?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Collapse to an icon-only button on mobile (default true). */
  collapseOnMobile?: boolean;
  /** Extra classes applied only to the mobile icon-only button. */
  mobileClassName?: string;
}

/**
 * Standard "actions" dropdown trigger — a labeled button that collapses to a
 * solid icon-only button on mobile. Pair it with `<Menu>` anchored to the
 * element returned by the click handler (`e.currentTarget`).
 */
const MenuButton = ({
  label,
  onClick,
  icon,
  variant = "primary",
  size = "md",
  collapseOnMobile = true,
  className,
  mobileClassName,
  ...rest
}: MenuButtonProps) => {
  const isMobile = useMediaQuery("(max-width:599.95px)");
  const iconNode = icon ?? <DotsVerticalIcon className="h-[18px] w-[18px]" />;

  if (collapseOnMobile && isMobile) {
    return (
      <IconButton
        aria-label={label}
        icon={iconNode}
        variant="solid"
        size="md"
        onClick={onClick}
        className={cn(mobileClassName, className)}
        {...rest}
      />
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      prefixIcon={iconNode}
      onClick={onClick}
      className={cn("whitespace-nowrap", className)}
      {...rest}
    >
      {label}
    </Button>
  );
};

export default MenuButton;
